import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TenantThemeConfig } from '../types/theme'
import { hasActiveLiveTv, countActiveLiveStreams } from '../lib/liveTv'

interface DraggableLiveTvFloatProps {
  themeConfig: TenantThemeConfig
  bottomOffset?: number
}

interface FloatPosition {
  x: number
  y: number
}

const FLOAT_SIZE = 58
const DRAG_THRESHOLD = 8

function storageKey(tenantId: string) {
  return `live-tv-float-pos:${tenantId}`
}

function getDefaultPosition(bottomOffset: number): FloatPosition {
  if (typeof window === 'undefined') return { x: 16, y: 100 }
  return {
    x: 16,
    y: Math.max(16, window.innerHeight - FLOAT_SIZE - bottomOffset),
  }
}

function clampPosition(pos: FloatPosition, bottomOffset: number): FloatPosition {
  const maxX = Math.max(8, window.innerWidth - FLOAT_SIZE - 8)
  const maxY = Math.max(8, window.innerHeight - FLOAT_SIZE - bottomOffset)
  return {
    x: Math.min(Math.max(8, pos.x), maxX),
    y: Math.min(Math.max(8, pos.y), maxY),
  }
}

const DraggableLiveTvFloat: React.FC<DraggableLiveTvFloatProps> = ({
  themeConfig,
  bottomOffset = 24,
}) => {
  const navigate = useNavigate()
  const channels = themeConfig.settings.liveTvChannels || []
  const huayYoutubeLive = themeConfig.settings.huayYoutubeLive
  const visible = hasActiveLiveTv(channels, themeConfig.settings.liveTvEnabled, huayYoutubeLive)

  const [position, setPosition] = useState<FloatPosition>(() => getDefaultPosition(bottomOffset))
  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    pointerId: -1,
  })

  useEffect(() => {
    if (!visible) return
    try {
      const raw = localStorage.getItem(storageKey(themeConfig.tenantId))
      if (!raw) {
        setPosition(clampPosition(getDefaultPosition(bottomOffset), bottomOffset))
        return
      }
      const parsed = JSON.parse(raw) as FloatPosition
      setPosition(clampPosition(parsed, bottomOffset))
    } catch {
      setPosition(clampPosition(getDefaultPosition(bottomOffset), bottomOffset))
    }
  }, [visible, themeConfig.tenantId, bottomOffset])

  useEffect(() => {
    if (!visible) return
    const onResize = () => {
      setPosition((prev) => clampPosition(prev, bottomOffset))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [visible, bottomOffset])

  const persistPosition = useCallback((pos: FloatPosition) => {
    localStorage.setItem(storageKey(themeConfig.tenantId), JSON.stringify(pos))
  }, [themeConfig.tenantId])

  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    dragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
      pointerId: event.pointerId,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) return
    const dx = event.clientX - dragRef.current.startX
    const dy = event.clientY - dragRef.current.startY
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      dragRef.current.moved = true
    }
    const next = clampPosition({
      x: dragRef.current.originX + dx,
      y: dragRef.current.originY + dy,
    }, bottomOffset)
    setPosition(next)
  }

  const onPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) return
    const wasDrag = dragRef.current.moved
    dragRef.current.active = false
    event.currentTarget.releasePointerCapture(event.pointerId)
    const finalPos = clampPosition({
      x: dragRef.current.originX + (event.clientX - dragRef.current.startX),
      y: dragRef.current.originY + (event.clientY - dragRef.current.startY),
    }, bottomOffset)
    setPosition(finalPos)
    persistPosition(finalPos)
    if (!wasDrag) {
      navigate('/live-tv')
    }
  }

  const liveCount = useMemo(
    () => countActiveLiveStreams(channels, themeConfig.settings.liveTvEnabled, huayYoutubeLive),
    [channels, themeConfig.settings.liveTvEnabled, huayYoutubeLive]
  )

  if (!visible) return null

  return (
    <button
      type="button"
      aria-label="เปิดหน้าดูถ่ายทอดสด"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${FLOAT_SIZE}px`,
        height: `${FLOAT_SIZE}px`,
        zIndex: 9998,
        border: 'none',
        borderRadius: '9999px',
        padding: 0,
        cursor: 'grab',
        touchAction: 'none',
        background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
        boxShadow: '0 10px 28px rgba(239, 68, 68, 0.45)',
        color: '#fff',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 0 0 0 rgba(255,255,255,0.8)',
          animation: 'liveTvPulse 1.4s infinite',
        }}
      />
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}>
        <i className="fas fa-tv" style={{ fontSize: 18, marginBottom: 2 }} />
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6 }}>LIVE</span>
      </span>
      {liveCount > 1 && (
        <span
          style={{
            position: 'absolute',
            left: -4,
            top: -4,
            minWidth: 18,
            height: 18,
            borderRadius: 999,
            background: '#111827',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
          }}
        >
          {liveCount}
        </span>
      )}
      <style>{`
        @keyframes liveTvPulse {
          0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
          70% { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
          100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
        }
      `}</style>
    </button>
  )
}

export default DraggableLiveTvFloat
